import React, { useState, useEffect } from 'react';
import { ref, get, child, getDatabase, remove, update } from 'firebase/database';
import { database, auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import AdminAnnouncements from '../components/AdminAnnouncements';
import SlideshowManager from '../components/admin/SlideshowManager';
import OrgChartManager from '../components/admin/OrgChartManager';
import EmailTest from '../components/EmailTest';
import RotcForm from '../components/RotcForm';
import CwtsForm from '../components/CwtsForm';
import { handlePrint } from '../utils/printUtils';
import './AdminDashboard.css';

function AdminDashboard() {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');
  
  // Set default tab based on URL query parameter
  const [activeTab, setActiveTab] = useState(tabParam || 'students');
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewStudent, setViewStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [timePeriod, setTimePeriod] = useState('all-time');
  const [showRotcForm, setShowRotcForm] = useState(false);
  const [showCwtsForm, setShowCwtsForm] = useState(false);

  // Handle tab changes and update URL
  useEffect(() => {
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/admin${tab === 'students' ? '' : `?tab=${tab}`}`, { replace: true });
  };

  // Fetch all students from the database
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      setError('');
      
      // Check if user is authenticated as admin
      if (!currentUser || !isAdmin) {
        setError('Authentication required. Please login as admin.');
        setLoading(false);
        return;
      }
      
      try {
        console.log('Fetching students data...');
        // Use the database reference directly
        const dbRef = ref(getDatabase());
        
        // Change the path to a public path that doesn't require special permissions
        // or try using the admin's authentication
        const snapshot = await get(child(dbRef, 'students'));
        
        if (snapshot.exists()) {
          console.log('Students data found');
          const studentsData = snapshot.val();
          
          // Convert object to array and ensure all required fields exist
          const studentsArray = Object.keys(studentsData).map(key => {
            const student = studentsData[key];
            // Ensure all required fields exist with defaults
            return {
              id: key,
              firstName: student.firstName || '',
              middleName: student.middleName || '',
              lastName: student.lastName || '',
              sex: student.sex || student.gender || '',
              course: student.course || '',
              contactNumber: student.contactNumber || '',
              email: student.email || '',
              nstpComponent: student.nstpComponent || '',
              emergencyContactName: student.emergencyContactName || '',
              emergencyContactNumber: student.emergencyContactNumber || '',
              registrationDate: student.registrationDate || new Date().toISOString(),
              address: student.address || '',
              birthdate: student.birthdate || '',
              studentId: student.studentId || '',
              ...student  // Keep any other fields from the original data
            };
          });
          
          console.log(`Loaded ${studentsArray.length} students`, studentsArray);
          setStudents(studentsArray);
          setError(''); // Clear any previous errors
        } else {
          console.log('No students data found');
          // No data exists, but this is not an error
          setStudents([]);
          setError('');
        }
      } catch (err) {
        console.error('Error fetching students:', err);
        
        // Handle permission denied specifically
        if (err.message.includes('permission_denied') || err.message.includes('Permission denied')) {
          setError('Permission denied. Please check Firebase database rules or admin privileges.');
        } else {
          setError('Failed to load students data. Error: ' + err.message);
        }
        
        setStudents([]); // Reset students on error
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'students') {
      fetchStudents();
    }
  }, [currentUser, isAdmin, activeTab]);

  // Filter students based on NSTP component
  const filteredStudents = students.filter(student => {
    const matchesFilter = filter === 'all' || student.nstpComponent === filter;
    
    // If search term exists, check if it matches name or course
    const matchesSearch = searchTerm === '' || 
      student.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.course && student.course.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  // Handle viewing student details
  const handleViewStudent = (student) => {
    console.log('Viewing student details:', student);
    // Make sure we have a valid student object with all required fields
    setViewStudent({
      id: student.id,
      firstName: student.firstName || '',
      middleName: student.middleName || '',
      lastName: student.lastName || '',
      sex: student.sex || student.gender || '',
      course: student.course || '',
      contactNumber: student.contactNumber || '',
      email: student.email || '',
      nstpComponent: student.nstpComponent || '',
      emergencyContactName: student.emergencyContactName || '',
      emergencyContactNumber: student.emergencyContactNumber || '',
      registrationDate: student.registrationDate || new Date().toISOString(),
      address: student.address || '',
      birthdate: student.birthdate || '',
      studentId: student.studentId || ''
    });
  };

  // Handle editing student
  const handleEditStudent = (student) => {
    setEditStudent({...student});
  };

  // Handle updating student data
  const handleUpdateStudent = async () => {
    try {
      const dbRef = ref(getDatabase(), `students/${editStudent.id}`);
      await update(dbRef, editStudent);
      
      // Update local state
      setStudents(students.map(student => 
        student.id === editStudent.id ? editStudent : student
      ));
      
      // Close edit modal
      setEditStudent(null);
    } catch (err) {
      setError('Failed to update student: ' + err.message);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (student) => {
    setStudentToDelete(student);
    setShowConfirmDelete(true);
  };

  // Handle actual deletion
  const handleDeleteStudent = async () => {
    try {
      const dbRef = ref(getDatabase(), `students/${studentToDelete.id}`);
      await remove(dbRef);
      
      // Update local state
      setStudents(students.filter(student => student.id !== studentToDelete.id));
      
      // Close delete confirmation
      setShowConfirmDelete(false);
      setStudentToDelete(null);
    } catch (err) {
      setError('Failed to delete student: ' + err.message);
    }
  };

  // Handle print functionality specifically for ROTC form
  const handlePrintRotcForm = () => {
    try {
      console.log('Initiating ROTC form print process');
      
      // Make sure the ROTC form is visible
      if (!showRotcForm) {
        console.log('ROTC form was not visible, showing it first');
        setShowRotcForm(true);
        
        // Give a longer delay to ensure the component renders before printing
        setTimeout(() => {
          console.log('Now printing after form is shown');
          
          // Find the ROTC form element and mark it for printing
          const form = document.querySelector('.rotc-form');
          if (form) {
            console.log('Found ROTC form, marking for print');
            form.setAttribute('data-print-target', 'true');
            
            // Set background to white
            document.body.style.backgroundColor = 'white';
            
            // Use the print utility
            import('../utils/printUtils').then(({ handlePrint }) => {
              console.log('Print utility loaded, calling handlePrint');
              handlePrint();
              
              // Clean up after printing
              setTimeout(() => {
                if (form) {
                  form.removeAttribute('data-print-target');
                }
              }, 2000);
            }).catch(err => {
              console.error('Failed to load print utility:', err);
              window.print(); // Fallback
            });
          } else {
            console.error('Could not find form element to print');
            window.print(); // Fallback
          }
        }, 1000); // Increased timeout to ensure rendering completes
      } else {
        // If already visible, print immediately
        console.log('ROTC form already visible, printing directly');
        
        // Find the ROTC form element and mark it for printing
        const form = document.querySelector('.rotc-form');
        if (form) {
          console.log('Found ROTC form, marking for print');
          form.setAttribute('data-print-target', 'true');
          
          // Set background to white
          document.body.style.backgroundColor = 'white';
          
          // Use the print utility
          import('../utils/printUtils').then(({ handlePrint }) => {
            console.log('Print utility loaded, calling handlePrint');
            handlePrint();
            
            // Clean up after printing
            setTimeout(() => {
              if (form) {
                form.removeAttribute('data-print-target');
              }
            }, 2000);
          }).catch(err => {
            console.error('Failed to load print utility:', err);
            window.print(); // Fallback
          });
        } else {
          console.error('Could not find form element to print');
          window.print(); // Fallback
        }
      }
    } catch (error) {
      console.error('Error attempting to print ROTC form:', error);
      // Fallback to regular window print if error occurs
      window.print();
    }
  };

  // Handle print functionality specifically for CWTS form
  const handlePrintCwtsForm = () => {
    try {
      console.log('Initiating CWTS form print process');
      
      // Make sure the CWTS form is visible
      if (!showCwtsForm) {
        console.log('CWTS form was not visible, showing it first');
        setShowCwtsForm(true);
        
        // Give a longer delay to ensure the component renders before printing
        setTimeout(() => {
          console.log('Now printing after form is shown');
          
          // Import the print utility function
          import('../utils/printUtils').then(({ handlePrint }) => {
            console.log('Print utility loaded, calling handlePrint');
            handlePrint();
          }).catch(err => {
            console.error('Failed to load print utility:', err);
            window.print(); // Fallback
          });
        }, 800); // Increased timeout to ensure rendering completes
      } else {
        // If already visible, print immediately
        console.log('CWTS form already visible, printing directly');
        
        // Import the print utility function
        import('../utils/printUtils').then(({ handlePrint }) => {
          console.log('Print utility loaded, calling handlePrint');
          handlePrint();
        }).catch(err => {
          console.error('Failed to load print utility:', err);
          window.print(); // Fallback
        });
      }
    } catch (error) {
      console.error('Error attempting to print CWTS form:', error);
      // Fallback to regular window print if error occurs
      window.print();
    }
  };

  // Close modals
  const handleCloseModals = () => {
    setViewStudent(null);
    setEditStudent(null);
    setShowConfirmDelete(false);
    setStudentToDelete(null);
    setShowRotcForm(false);
    setShowCwtsForm(false);
  };

  // Handle export functionality (not implemented yet)
  const handleExport = () => {
    alert('Export to Excel functionality will be implemented soon.');
  };

  // Handle print functionality
  const handlePrint = () => {
    window.print();
  };

  // Handle loading state
  if (loading && activeTab === 'students') {
    return (
      <AdminLayout>
        <div className="admin-container loading">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-container">
        <div className="admin-header">
          <h2 className="page-title">NSTP Admin Dashboard</h2>
          <p>Manage registrations, announcements, and system settings</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        
        <div className="admin-tabs">
          <button 
            className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => handleTabChange('students')}
          >
            Students
          </button>
          <button 
            className={`tab-btn ${activeTab === 'announcements' ? 'active' : ''}`}
            onClick={() => handleTabChange('announcements')}
          >
            Announcements
          </button>
          <button 
            className={`tab-btn ${activeTab === 'slideshow' ? 'active' : ''}`}
            onClick={() => handleTabChange('slideshow')}
          >
            Slideshow
          </button>
          <button 
            className={`tab-btn ${activeTab === 'orgchart' ? 'active' : ''}`}
            onClick={() => handleTabChange('orgchart')}
          >
            Organizational Chart
          </button>
          <button 
            className={`tab-btn ${activeTab === 'emailtest' ? 'active' : ''}`}
            onClick={() => handleTabChange('emailtest')}
          >
            Email Test
          </button>
        </div>

        {activeTab === 'students' && (
          <>
            <div className="statistics-menu">
              <div className="menu-left">
                <div className="stat-period-selector">
                  <label>Time Period:</label>
                  <select 
                    value={timePeriod} 
                    onChange={(e) => setTimePeriod(e.target.value)}
                  >
                    <option value="all-time">All Time</option>
                    <option value="current-semester">Current Semester</option>
                    <option value="last-semester">Last Semester</option>
                    <option value="current-year">Current Year</option>
                  </select>
                </div>
                <div className="stat-filter-buttons">
                  <button 
                    className={`stat-filter-btn ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                  >
                    All
                  </button>
                  <button 
                    className={`stat-filter-btn ${filter === 'ROTC' ? 'active' : ''}`}
                    onClick={() => setFilter('ROTC')}
                  >
                    ROTC
                  </button>
                  <button 
                    className={`stat-filter-btn ${filter === 'CWTS' ? 'active' : ''}`}
                    onClick={() => setFilter('CWTS')}
                  >
                    CWTS
                  </button>
                </div>
              </div>
              <div className="menu-right">
                <button className="stat-export-btn" onClick={handleExport}>
                  <i>📊</i> Export to Excel
                </button>
                <button className="stat-print-btn" onClick={handlePrint}>
                  <i>🖨️</i> Print
                </button>
              </div>
            </div>

            <div className="admin-content">
              <div className="admin-controls">
                <div className="search-box">
                  <input 
                    type="text" 
                    placeholder="Search by name or course" 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="students-table-container">
                <table className="students-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Sex</th>
                      <th>Course</th>
                      <th>Contact</th>
                      <th>Email</th>
                      <th>NSTP Component</th>
                      <th>Emergency Contact</th>
                      <th>Registration Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.length > 0 ? (
                      filteredStudents.map(student => (
                        <tr key={student.id}>
                          <td>
                            {`${student.lastName || ''}, ${student.firstName || ''}${student.middleName ? ' ' + student.middleName : ''}`}
                          </td>
                          <td>{student.sex || 'N/A'}</td>
                          <td>{student.course || 'N/A'}</td>
                          <td>{student.contactNumber || 'N/A'}</td>
                          <td>{student.email || 'N/A'}</td>
                          <td>
                            {student.nstpComponent ? (
                              <span className={`badge badge-${student.nstpComponent.toLowerCase()}`}>
                                {student.nstpComponent}
                              </span>
                            ) : 'N/A'}
                          </td>
                          <td>
                            {student.emergencyContactName || 'N/A'}<br />
                            {student.emergencyContactNumber && (
                              <span className="small-text">{student.emergencyContactNumber}</span>
                            )}
                          </td>
                          <td>
                            {student.registrationDate ? (
                              new Date(student.registrationDate).toLocaleDateString()
                            ) : 'N/A'}
                          </td>
                          <td className="action-buttons">
                            <button 
                              className="btn btn-view" 
                              onClick={() => handleViewStudent(student)}
                              title="View Details"
                            >
                              View
                            </button>
                            <button 
                              className="btn btn-edit" 
                              onClick={() => handleEditStudent(student)}
                              title="Edit Student"
                            >
                              Edit
                            </button>
                            <button 
                              className="btn btn-delete" 
                              onClick={() => handleDeleteConfirm(student)}
                              title="Delete Student"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="no-data">
                          {searchTerm || filter !== 'all' ? 'No matching students found.' : 'No students registered yet.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* View Student Modal */}
              {viewStudent && (
                <div className="modal-backdrop">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h3>Student Details</h3>
                      <button className="close-btn" onClick={handleCloseModals}>&times;</button>
                    </div>
                    <div className="modal-body">
                      <div className="student-details">
                        <div className="detail-row">
                          <strong>Name:</strong> 
                          <span>{`${viewStudent.lastName || ''}, ${viewStudent.firstName || ''}${viewStudent.middleName ? ' ' + viewStudent.middleName : ''}`}</span>
                        </div>
                        <div className="detail-row">
                          <strong>Sex:</strong> <span>{viewStudent.sex || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <strong>Course:</strong> <span>{viewStudent.course || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <strong>Contact Number:</strong> <span>{viewStudent.contactNumber || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <strong>Email:</strong> <span>{viewStudent.email || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <strong>NSTP Component:</strong> 
                          <span>{viewStudent.nstpComponent || 'N/A'}</span>
                          {viewStudent.nstpComponent === 'ROTC' && (
                            <button 
                              className="rotc-form-toggle" 
                              onClick={() => setShowRotcForm(!showRotcForm)}
                            >
                              {showRotcForm ? 'Hide ROTC Form' : 'Show ROTC Form'}
                            </button>
                          )}
                          {viewStudent.nstpComponent === 'CWTS' && (
                            <button 
                              className="cwts-form-toggle" 
                              onClick={() => setShowCwtsForm(!showCwtsForm)}
                            >
                              {showCwtsForm ? 'Hide CWTS Form' : 'Show CWTS Form'}
                            </button>
                          )}
                        </div>
                        <div className="detail-row">
                          <strong>Emergency Contact:</strong> 
                          <span>{viewStudent.emergencyContactName || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <strong>Emergency Contact Number:</strong> 
                          <span>{viewStudent.emergencyContactNumber || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <strong>Registration Date:</strong> 
                          <span>{viewStudent.registrationDate ? new Date(viewStudent.registrationDate).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <strong>Address:</strong> <span>{viewStudent.address || 'N/A'}</span>
                        </div>
                      </div>
                      
                      {/* ROTC Form for ROTC students */}
                      {viewStudent && viewStudent.nstpComponent === 'ROTC' && showRotcForm && (
                        <div className="student-rotc-form">
                          {console.log('Rendering ROTC form with data:', viewStudent)}
                          <RotcForm studentData={viewStudent} />
                        </div>
                      )}

                      {/* CWTS Form for CWTS students */}
                      {viewStudent && viewStudent.nstpComponent === 'CWTS' && showCwtsForm && (
                        <div className="student-cwts-form">
                          {console.log('Rendering CWTS form with data:', viewStudent)}
                          <CwtsForm studentData={viewStudent} />
                        </div>
                      )}
                    </div>
                    <div className="modal-footer">
                      {viewStudent && viewStudent.nstpComponent === 'ROTC' && showRotcForm && (
                        <button className="btn btn-primary" onClick={handlePrintRotcForm}>
                          Print ROTC Form
                        </button>
                      )}
                      {viewStudent && viewStudent.nstpComponent === 'CWTS' && showCwtsForm && (
                        <button className="btn btn-primary" onClick={handlePrintCwtsForm}>
                          Print CWTS Form
                        </button>
                      )}
                      <button className="btn btn-secondary" onClick={handleCloseModals}>Close</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Student Modal */}
              {editStudent && (
                <div className="modal-backdrop">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h3>Edit Student</h3>
                      <button className="close-btn" onClick={handleCloseModals}>&times;</button>
                    </div>
                    <div className="modal-body">
                      <div className="form-group">
                        <label>First Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStudent.firstName || ''}
                          onChange={(e) => setEditStudent({...editStudent, firstName: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Middle Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStudent.middleName || ''} 
                          onChange={(e) => setEditStudent({...editStudent, middleName: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Last Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStudent.lastName || ''} 
                          onChange={(e) => setEditStudent({...editStudent, lastName: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Sex:</label>
                        <select
                          className="form-control"
                          value={editStudent.sex || ''}
                          onChange={(e) => setEditStudent({...editStudent, sex: e.target.value})}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Course:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStudent.course || ''}
                          onChange={(e) => setEditStudent({...editStudent, course: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Contact Number:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStudent.contactNumber || ''}
                          onChange={(e) => setEditStudent({...editStudent, contactNumber: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Email:</label>
                        <input
                          type="email"
                          className="form-control"
                          value={editStudent.email || ''}
                          onChange={(e) => setEditStudent({...editStudent, email: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>NSTP Component:</label>
                        <select
                          className="form-control"
                          value={editStudent.nstpComponent || ''}
                          onChange={(e) => setEditStudent({...editStudent, nstpComponent: e.target.value})}
                        >
                          <option value="">Select NSTP Component</option>
                          <option value="ROTC">ROTC</option>
                          <option value="CWTS">CWTS</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Emergency Contact Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStudent.emergencyContactName || ''}
                          onChange={(e) => setEditStudent({...editStudent, emergencyContactName: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Emergency Contact Number:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStudent.emergencyContactNumber || ''}
                          onChange={(e) => setEditStudent({...editStudent, emergencyContactNumber: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Address:</label>
                        <textarea
                          className="form-control"
                          value={editStudent.address || ''}
                          onChange={(e) => setEditStudent({...editStudent, address: e.target.value})}
                        ></textarea>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-primary" onClick={handleUpdateStudent}>Save Changes</button>
                      <button className="btn btn-secondary" onClick={handleCloseModals}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              {showConfirmDelete && (
                <div className="modal-backdrop">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h3>Confirm Delete</h3>
                      <button className="close-btn" onClick={handleCloseModals}>&times;</button>
                    </div>
                    <div className="modal-body">
                      <p className="warning">
                        Are you sure you want to delete {studentToDelete?.firstName} {studentToDelete?.lastName}'s record? 
                        This action cannot be undone.
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-danger" onClick={handleDeleteStudent}>Delete</button>
                      <button className="btn btn-secondary" onClick={handleCloseModals}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        
        {activeTab === 'announcements' && (
          <>
            <div className="statistics-menu">
              <div className="menu-left">
                <div className="stat-filter-buttons">
                  <button className="stat-filter-btn active">All Announcements</button>
                  <button className="stat-filter-btn">General</button>
                  <button className="stat-filter-btn">Events</button>
                  <button className="stat-filter-btn">Notices</button>
                </div>
              </div>
              <div className="menu-right">
                <button className="stat-export-btn" onClick={handleExport}>
                  <i>📊</i> Export
                </button>
                <button className="stat-print-btn" onClick={handlePrint}>
                  <i>🖨️</i> Print
                </button>
              </div>
            </div>
            <div className="admin-content">
              <AdminAnnouncements />
            </div>
          </>
        )}
        
        {activeTab === 'slideshow' && (
          <>
            <div className="statistics-menu">
              <div className="menu-left">
                <div className="stat-filter-buttons">
                  <button className="stat-filter-btn active">All Slides</button>
                  <button className="stat-filter-btn">Active</button>
                  <button className="stat-filter-btn">Inactive</button>
                </div>
              </div>
              <div className="menu-right">
                <button className="stat-export-btn" onClick={handleExport}>
                  <i>📊</i> Export
                </button>
                <button className="stat-print-btn" onClick={handlePrint}>
                  <i>🖨️</i> Print
                </button>
              </div>
            </div>
            <div className="admin-content">
              <SlideshowManager />
            </div>
          </>
        )}
        
        {activeTab === 'orgchart' && (
          <>
            <div className="statistics-menu">
              <div className="menu-left">
                <div className="stat-filter-buttons">
                  <button className="stat-filter-btn active">All Departments</button>
                  <button className="stat-filter-btn">Administration</button>
                  <button className="stat-filter-btn">Faculty</button>
                  <button className="stat-filter-btn">Staff</button>
                </div>
              </div>
              <div className="menu-right">
                <button className="stat-export-btn" onClick={handleExport}>
                  <i>📊</i> Export
                </button>
                <button className="stat-print-btn" onClick={handlePrint}>
                  <i>🖨️</i> Print
                </button>
              </div>
            </div>
            <div className="admin-content">
              <OrgChartManager />
            </div>
          </>
        )}
        
        {activeTab === 'emailtest' && (
          <>
            <div className="admin-toolbar">
              <h3>Email Communication System</h3>
              <div className="admin-toolbar-actions">
                <button onClick={() => console.log('Email settings')} className="btn btn-outline-secondary">
                  <i className="bi bi-gear"></i> Settings
                </button>
              </div>
            </div>
            <div className="admin-content">
              <EmailTest />
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminDashboard; 